import React, { useCallback, useEffect, useMemo, useState } from "react";
import PageTitle from "../../PageTitle";
import useAxios from "../../../utilities/useAxios";
import {
	Badge,
	Button,
	Card,
	Checkbox,
	ConfigProvider,
	Layout,
	message,
	Modal,
	Input,
	Space,
	Tabs,
	Tag,
	Typography,
	Spin,
	Empty,
} from "antd";
import { DownOutlined, RightOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";
import PageHeader from "../../common/PageHeader";

dayjs.locale("ko");

const { Content } = Layout;
const { TextArea } = Input;
const { Text } = Typography;

const TYPE_TABS = [
	{
		key: "annual",
		label: "연차/반차",
		types: ["연차", "오전반차", "오후반차", "월차"],
	},
	{ key: "trip", label: "출장", types: ["출장"] },
	{ key: "family", label: "경조휴가", types: ["경조사"] },
	{ key: "sick", label: "병가", types: ["병가"] },
	{ key: "menstrual", label: "생리휴가", types: ["생휴"] },
	{ key: "local", label: "시내출장", types: ["시내출장"] },
];

const REPORT_ONLY_TYPES = new Set(["생휴", "시내출장"]);

const statusTag = (status) => {
	const map = {
		pending: { color: "gold", text: "대기" },
		approved: { color: "green", text: "승인" },
		rejected: { color: "red", text: "반려" },
		not_required: { color: "green", text: "승인" },
	};
	const item = map[status] || map.pending;
	return <Tag color={item.color}>{item.text}</Tag>;
};

const leaveTypeLabel = (t) => {
	const map = {
		오전반차: "오전 반차",
		오후반차: "오후 반차",
		경조사: "경조휴가",
		생휴: "생리휴가",
	};
	return map[t] || t;
};

const getJobLabel = (job, gender) => {
	if (job === "목사") {
		return "목사";
	}
	if (job === "전도사") {
		return gender === "남" ? "남전도사" : "여전도사";
	}
	if (job === "교직원") {
		return gender === "남" ? "남직원" : "여직원";
	}
	return job || "-";
};

const formatPeriod = (r) => {
	if (r.start_date && r.end_date) {
		if (r.start_date === r.end_date) {
			return r.start_date;
		}
		return `${r.start_date} ~ ${r.end_date}`;
	}
	return r.leave_date;
};

const companionsLabel = (record) => {
	const info = record.additional_users_info || [];
	if (!info.length) {
		return null;
	}
	return info.map((u) => u.name).join(", ");
};

const isReportOnly = (record) =>
	REPORT_ONLY_TYPES.has(record.leave_type);

const isSelectable = (record) =>
	isReportOnly(record) || Boolean(record.approval_stage);

const tabLabelWithBadge = (label, count) => {
	if (!count) {
		return label;
	}
	return (
		<span>
			{label}{" "}
			<Badge
				count={count}
				overflowCount={99}
				size="small"
				style={{ marginLeft: 4 }}
			/>
		</span>
	);
};

const LeaveApprovals = () => {
	const [mode, setMode] = useState("pending");
	const [typeTab, setTypeTab] = useState("annual");
	const [rows, setRows] = useState([]);
	const [pendingRows, setPendingRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [capability, setCapability] = useState(null);
	const [rejectOpen, setRejectOpen] = useState(false);
	const [rejectTarget, setRejectTarget] = useState(null);
	const [rejectReason, setRejectReason] = useState("");
	const [expandedIds, setExpandedIds] = useState(() => new Set());
	const [selectedIds, setSelectedIds] = useState(() => new Set());
	const [bulkLoading, setBulkLoading] = useState(false);
	const [searchText, setSearchText] = useState("");
	const [isWide, setIsWide] = useState(
		() =>
			typeof window !== "undefined" &&
			window.matchMedia("(min-width: 992px)").matches,
	);

	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}
		const mq = window.matchMedia("(min-width: 992px)");
		const sync = () => setIsWide(mq.matches);
		sync();
		mq.addEventListener("change", sync);
		return () => mq.removeEventListener("change", sync);
	}, []);

	const loadCapability = useCallback(async () => {
		try {
			const res = await useAxios.get(
				"/utils/leave/approvals/capability/",
			);
			setCapability(res.data);
		} catch (e) {
			console.error(e);
		}
	}, []);

	const loadPendingBadgeRows = useCallback(async () => {
		try {
			const res = await useAxios.get(
				"/utils/leave/approvals/pending/",
				{ params: { tab: "pending" } },
			);
			setPendingRows(res.data || []);
		} catch (e) {
			console.error(e);
		}
	}, []);

	const loadRows = useCallback(async () => {
		setLoading(true);
		try {
			const res = await useAxios.get(
				"/utils/leave/approvals/pending/",
				{ params: { tab: mode } },
			);
			setRows(res.data || []);
			if (mode === "pending") {
				setPendingRows(res.data || []);
			} else {
				loadPendingBadgeRows();
			}
		} catch (e) {
			message.error("결재 목록을 불러오지 못했습니다.");
		} finally {
			setLoading(false);
		}
	}, [mode, loadPendingBadgeRows]);

	useEffect(() => {
		loadCapability();
	}, [loadCapability]);

	useEffect(() => {
		if (capability) {
			loadRows();
		}
	}, [capability, loadRows]);

	useEffect(() => {
		setSelectedIds(new Set());
		setExpandedIds(new Set());
	}, [mode, typeTab, searchText]);

	const filtered = useMemo(() => {
		const tab = TYPE_TABS.find((t) => t.key === typeTab);
		const types = tab?.types || [];
		const keyword = searchText.trim().toLowerCase();
		return rows.filter((r) => {
			if (!types.includes(r.leave_type)) {
				return false;
			}
			if (!keyword) {
				return true;
			}
			const haystack = [
				r.user_name,
				getJobLabel(r.user_job, r.user_gender),
				leaveTypeLabel(r.leave_type),
				formatPeriod(r),
				r.reason,
				r.trip_title,
				r.trip_location,
			]
				.filter(Boolean)
				.join(" ")
				.toLowerCase();
			return haystack.includes(keyword);
		});
	}, [rows, typeTab, searchText]);

	const pendingCounts = useMemo(() => {
		const byType = {};
		for (const t of TYPE_TABS) {
			byType[t.key] = 0;
		}
		let total = 0;
		for (const r of pendingRows) {
			const tab = TYPE_TABS.find((t) =>
				t.types.includes(r.leave_type),
			);
			if (tab) {
				byType[tab.key] += 1;
				total += 1;
			}
		}
		return {
			total,
			byType,
		};
	}, [pendingRows]);

	const selectableFiltered = useMemo(
		() => filtered.filter(isSelectable),
		[filtered],
	);

	const decide = async (id, decision, reason = "") => {
		try {
			await useAxios.post(
				`/utils/leave/approvals/${id}/decide/`,
				{ decision, reason },
			);
			message.success(
				decision === "approve"
					? "승인했습니다."
					: decision === "confirm"
						? "확인했습니다."
						: "반려했습니다.",
			);
			loadRows();
			return true;
		} catch (e) {
			message.error(
				e.response?.data?.error ||
					"결재 처리에 실패했습니다.",
			);
			return false;
		}
	};

	const confirmApprove = (record) => {
		Modal.confirm({
			title: "승인하시겠습니까?",
			okText: "승인",
			cancelText: "취소",
			onOk: () => decide(record.id, "approve"),
		});
	};

	const confirmReport = (record) => {
		Modal.confirm({
			title: "확인하시겠습니까?",
			content: "확인하면 이전 탭으로 이동합니다.",
			okText: "확인",
			cancelText: "취소",
			onOk: () => decide(record.id, "confirm"),
		});
	};

	const openReject = (record) => {
		setRejectTarget(record);
		setRejectReason("");
		setRejectOpen(true);
	};

	const toggleExpand = (id) => {
		setExpandedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) {
				next.delete(id);
			} else {
				next.add(id);
			}
			return next;
		});
	};

	const toggleSelect = (id, checked) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (checked) {
				next.add(id);
			} else {
				next.delete(id);
			}
			return next;
		});
	};

	const selectAll = () => {
		setSelectedIds(new Set(selectableFiltered.map((r) => r.id)));
	};

	const clearSelection = () => {
		setSelectedIds(new Set());
	};

	const bulkApprove = () => {
		const targets = selectableFiltered.filter((r) =>
			selectedIds.has(r.id),
		);
		if (!targets.length) {
			message.warning("처리할 항목을 선택해주세요.");
			return;
		}
		const reportBulk = targets.every((r) => isReportOnly(r));
		Modal.confirm({
			title: reportBulk
				? `선택한 ${targets.length}건을 확인하시겠습니까?`
				: `선택한 ${targets.length}건을 승인하시겠습니까?`,
			okText: reportBulk ? "일괄확인" : "일괄승인",
			cancelText: "취소",
			onOk: async () => {
				setBulkLoading(true);
				let ok = 0;
				let fail = 0;
				for (const record of targets) {
					try {
						await useAxios.post(
							`/utils/leave/approvals/${record.id}/decide/`,
							{
								decision: isReportOnly(record)
									? "confirm"
									: "approve",
							},
						);
						ok += 1;
					} catch {
						fail += 1;
					}
				}
				setBulkLoading(false);
				setSelectedIds(new Set());
				if (ok) {
					message.success(
						reportBulk
							? `${ok}건을 확인했습니다.`
							: `${ok}건을 승인했습니다.`,
					);
				}
				if (fail) {
					message.error(
						reportBulk
							? `${fail}건 확인에 실패했습니다.`
							: `${fail}건 승인에 실패했습니다.`,
					);
				}
				loadRows();
			},
		});
	};

	const renderActions = (record) => {
		if (isReportOnly(record)) {
			return (
				<Button
					type="primary"
					size="small"
					onClick={() => confirmReport(record)}>
					확인
				</Button>
			);
		}
		const canDecide = Boolean(record.approval_stage);
		if (!canDecide) {
			return <Tag>조회만 가능</Tag>;
		}
		return (
			<Space wrap onClick={(e) => e.stopPropagation()}>
				<Button
					type="primary"
					size="small"
					onClick={() => confirmApprove(record)}>
					승인
				</Button>
				<Button
					danger
					size="small"
					onClick={() => openReject(record)}>
					반려
				</Button>
			</Space>
		);
	};

	const approvalStepShortLabels = (steps) => {
		const sorted = [...(steps || [])].sort(
			(a, b) =>
				(a.order ?? 0) - (b.order ?? 0) ||
				(a.id ?? 0) - (b.id ?? 0),
		);
		if (sorted.length >= 3) {
			const labels = ["팀", "국", "당"];
			return sorted.map((s, i) => ({
				step: s,
				label: labels[i] || String(i + 1),
			}));
		}
		if (sorted.length === 2) {
			return [
				{ step: sorted[0], label: "국" },
				{ step: sorted[1], label: "당" },
			];
		}
		if (sorted.length === 1) {
			const only = sorted[0];
			const label =
				only.approver_kind === "team_leader" ? "팀" : "당";
			return [{ step: only, label }];
		}
		return [];
	};

	const renderApprovalStatus = (record) => {
		if (REPORT_ONLY_TYPES.has(record.leave_type)) {
			return null;
		}
		const steps = record.approval_steps || [];
		if (steps.length) {
			return (
				<div className="leave-approvals-status-row">
					{approvalStepShortLabels(steps).map(
						({ step, label }) => (
							<span key={step.id}>
								{label} {statusTag(step.status)}
							</span>
						),
					)}
				</div>
			);
		}
		return (
			<div className="leave-approvals-status-row">
				<span>팀 {statusTag(record.team_leader_status)}</span>
				<span>
					국 {statusTag(record.office_director_status)}
				</span>
				<span>
					당 {statusTag(record.church_leader_status)}
				</span>
			</div>
		);
	};

	const renderDetail = (record) => {
		const companions = companionsLabel(record);
		return (
			<div className="leave-approvals-detail">
				<div>
					<Text type="secondary">소속 </Text>
					<Text>
						{getJobLabel(
							record.user_job,
							record.user_gender,
						)}
					</Text>
				</div>
				<div>
					<Text type="secondary">사유/목적 </Text>
					<Text>{record.reason || "-"}</Text>
				</div>
				{companions && (
					<div>
						<Text type="secondary">동행인 </Text>
						<Text>{companions}</Text>
					</div>
				)}
				{record.trip_title && (
					<div>
						<Text type="secondary">출장 제목 </Text>
						<Text>{record.trip_title}</Text>
					</div>
				)}
				{record.trip_location && (
					<div>
						<Text type="secondary">지역 </Text>
						<Text>{record.trip_location}</Text>
					</div>
				)}
				{record.trip_background && (
					<div>
						<Text type="secondary">배경 </Text>
						<Text>{record.trip_background}</Text>
					</div>
				)}
				{renderApprovalStatus(record)}
				{mode === "pending" && (
					<div style={{ marginTop: 8 }}>
						{renderActions(record)}
					</div>
				)}
			</div>
		);
	};

	const renderList = () => {
		if (loading) {
			return (
				<div style={{ textAlign: "center", padding: 40 }}>
					<Spin />
				</div>
			);
		}
		if (!filtered.length) {
			return (
				<Empty
					description={
						searchText.trim()
							? "검색 결과가 없습니다."
							: "목록이 없습니다."
					}
				/>
			);
		}
		return (
			<div className="leave-approvals-cards">
				{filtered.map((record) => {
					const expanded = expandedIds.has(record.id);
					const selectable = isSelectable(record);
					return (
						<Card
							key={record.id}
							size="small"
							className="leave-approvals-card"
							styles={{ body: { padding: 12 } }}>
							<div
								className="leave-approvals-summary"
								onClick={() =>
									toggleExpand(record.id)
								}
								role="button"
								tabIndex={0}
								onKeyDown={(e) => {
									if (
										e.key === "Enter" ||
										e.key === " "
									) {
										e.preventDefault();
										toggleExpand(record.id);
									}
								}}>
								<div
									className="leave-approvals-summary-left"
									onClick={(e) =>
										e.stopPropagation()
									}>
									{mode === "pending" &&
										selectable && (
											<Checkbox
												checked={selectedIds.has(
													record.id,
												)}
												onChange={(e) =>
													toggleSelect(
														record.id,
														e.target
															.checked,
													)
												}
											/>
										)}
									{expanded ? (
										<DownOutlined />
									) : (
										<RightOutlined />
									)}
								</div>
								<div className="leave-approvals-summary-main">
									<div className="leave-approvals-summary-line">
										<Text strong>
											{record.user_name}
										</Text>
										<Text type="secondary">
											{getJobLabel(
												record.user_job,
												record.user_gender,
											)}
										</Text>
										<Tag>
											{leaveTypeLabel(
												record.leave_type,
											)}
										</Tag>
										<Text type="secondary">
											{formatPeriod(record)}
										</Text>
									</div>
									{isWide &&
										!REPORT_ONLY_TYPES.has(
											record.leave_type,
										) && (
											<div className="leave-approvals-summary-wide">
												{renderApprovalStatus(
													record,
												)}
											</div>
										)}
								</div>
							</div>
							{expanded && renderDetail(record)}
						</Card>
					);
				})}
			</div>
		);
	};

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title="연차 승인" />
			<Layout className="member-layout leave-approvals-page">
				<Layout className="member-content-layout leave-approvals-page">
					<Content className="member-content leave-approvals-page">
						<div className="page">
							<PageHeader
								title="연차 승인"
							/>
							<Card className="surface-card leave-approvals-card">
								<Tabs
									className="leave-approvals-mode-tabs"
									activeKey={mode}
									onChange={setMode}
									items={[
										{
											key: "pending",
											label: tabLabelWithBadge(
												"결재 대기",
												pendingCounts.total,
											),
										},
										{
											key: "history",
											label: "이전",
										},
									]}
								/>
								<div
									className="leave-approvals-type-bar"
									role="tablist">
									{TYPE_TABS.map((t) => {
										const count =
											mode === "history"
												? 0
												: pendingCounts.byType[
														t.key
													];
										const active =
											typeTab === t.key;
										return (
											<button
												key={t.key}
												type="button"
												role="tab"
												aria-selected={active}
												className={
													active
														? "leave-approvals-type-tab is-active"
														: "leave-approvals-type-tab"
												}
												onClick={() =>
													setTypeTab(t.key)
												}>
												{tabLabelWithBadge(
													t.label,
													count,
												)}
											</button>
										);
									})}
								</div>
								<div className="leave-approvals-search">
									<Input.Search
										allowClear
										placeholder="이름, 소속, 유형, 기간, 사유 검색"
										value={searchText}
										onChange={(e) =>
											setSearchText(e.target.value)
										}
									/>
								</div>
								{mode === "pending" && (
									<Space
										wrap
										className="leave-approvals-actions">
										<Button
											size="small"
											onClick={selectAll}
											disabled={
												!selectableFiltered.length
											}>
											모두선택
										</Button>
										<Button
											size="small"
											onClick={clearSelection}
											disabled={!selectedIds.size}>
											선택해제
										</Button>
										<Button
											type="primary"
											size="small"
											loading={bulkLoading}
											onClick={bulkApprove}
											disabled={!selectedIds.size}>
											{typeTab === "menstrual" ||
											typeTab === "local"
												? "일괄확인"
												: "일괄승인"}
										</Button>
									</Space>
								)}
								{renderList()}
							</Card>
						</div>
					</Content>
				</Layout>
			</Layout>
			<Modal
				title="반려 사유"
				open={rejectOpen}
				onOk={() => {
					if (rejectTarget) {
						decide(
							rejectTarget.id,
							"reject",
							rejectReason,
						);
					}
					setRejectOpen(false);
				}}
				onCancel={() => setRejectOpen(false)}
				okText="반려"
				okButtonProps={{ danger: true }}>
				<TextArea
					rows={3}
					value={rejectReason}
					onChange={(e) =>
						setRejectReason(e.target.value)
					}
					placeholder="반려 사유 (선택)"
				/>
			</Modal>
			<style>{`
				.leave-approvals-page {
					width: 100%;
					max-width: 100%;
					min-width: 0;
					box-sizing: border-box;
				}
				.leave-approvals-card {
					max-width: 100%;
					min-width: 0;
					border-radius: 8px;
				}
				.leave-approvals-card > .ant-card-body {
					max-width: 100%;
					min-width: 0;
					overflow-x: hidden;
					box-sizing: border-box;
				}
				.leave-approvals-mode-tabs,
				.leave-approvals-mode-tabs .ant-tabs-nav,
				.leave-approvals-mode-tabs .ant-tabs-nav-wrap {
					max-width: 100%;
					min-width: 0;
				}
				.leave-approvals-type-bar {
					display: flex;
					flex-wrap: wrap;
					align-items: flex-end;
					column-gap: 32px;
					row-gap: 0;
					max-width: 100%;
					margin: 0 0 16px;
					border-bottom: 1px solid #f0f0f0;
				}
				.leave-approvals-type-tab {
					display: inline-flex;
					flex: 0 0 auto;
					align-items: center;
					gap: 4px;
					margin: 0;
					padding: 12px 0 10px;
					border: none;
					border-bottom: 2px solid transparent;
					border-radius: 0;
					background: transparent;
					appearance: none;
					color: rgba(0, 0, 0, 0.88);
					font: inherit;
					font-size: 14px;
					line-height: 1.5;
					white-space: nowrap;
					cursor: pointer;
				}
				.leave-approvals-type-tab.is-active {
					color: #2457e6;
					border-bottom-color: #2457e6;
				}
				.leave-approvals-search {
					margin: 0 0 12px;
					max-width: 360px;
				}
				.ant-space.leave-approvals-actions {
					display: flex;
					width: 100%;
					max-width: 100%;
					margin-bottom: 12px;
				}
				.leave-approvals-cards {
					display: flex;
					flex-direction: column;
					gap: 10px;
					max-width: 100%;
					min-width: 0;
				}
				.leave-approvals-summary {
					display: flex;
					align-items: flex-start;
					gap: 10px;
					cursor: pointer;
				}
				.leave-approvals-summary-left {
					display: flex;
					align-items: center;
					gap: 8px;
					padding-top: 2px;
				}
				.leave-approvals-summary-main {
					flex: 1;
					min-width: 0;
				}
				.leave-approvals-summary-line {
					display: flex;
					flex-wrap: wrap;
					align-items: center;
					gap: 8px;
				}
				.leave-approvals-summary-wide {
					margin-top: 6px;
					display: flex;
					flex-wrap: wrap;
					align-items: center;
					gap: 8px;
				}
				.leave-approvals-detail {
					margin-top: 12px;
					padding-top: 12px;
					border-top: 1px solid #f0f0f0;
					display: flex;
					flex-direction: column;
					gap: 8px;
				}
				.leave-approvals-status-row {
					display: flex;
					flex-wrap: wrap;
					gap: 6px;
					align-items: center;
				}
				@media (max-width: 991px) {
					.leave-approvals-card > .ant-card-body {
						padding: 12px;
					}
					.leave-approvals-type-bar {
						column-gap: 12px;
					}
					.leave-approvals-type-tab {
						padding: 8px 0;
					}
				}
			`}</style>
		</ConfigProvider>
	);
};

export default LeaveApprovals;
